// ============================================================================
// RECSUIT ROLE PERMISSION ENGINE
// ============================================================================
// Pure TypeScript, zero React dependencies. Builds O(1) lookup Sets/Maps from
// the merged-permissions response (`/api/v1/Role/by-roles?roles=…`).
//
// This engine sits ALONGSIDE the legacy tenant-hub `PermissionEngine` in
// `permissionEngine.ts`. It is consumed by:
//   - <PermissionGate module=… action=…> for declarative gating in JSX
//   - useRolePermissions() hook for imperative checks
//   - main_layout.tsx sidebar to hide nav items the user can't see
//
// Module codes are defined in recsuit-api/recsuit.Core/Constants/RecsuitModuleCodes.cs
// and must match exactly between server-side [HasPermission] attributes,
// the seeded admin.tbl_role rows, and frontend gate calls.

import type { MergedPermissions } from '../api/types/role.types';

export interface RecsuitRoleEngine {
  /** True if the user's roles cover this module (visibility). */
  hasModule(moduleCode: string): boolean;

  /** True if the user's roles allow this (module, action) pair. */
  hasAction(moduleCode: string, actionCode: string): boolean;

  /** Set of all action codes the user has on the given module. */
  getActions(moduleCode: string): Set<string>;

  /** All module codes the user can see. */
  getVisibleModules(): Set<string>;

  /** Role codes that contributed to this engine. */
  getRoleCodes(): string[];

  /** True if this engine was built from a real permission set (not the empty default). */
  isLoaded(): boolean;
}

const EMPTY_ACTIONS: Set<string> = new Set();

export function createRecsuitRoleEngine(
  merged: MergedPermissions | null,
): RecsuitRoleEngine {
  const visibleModules = new Set<string>(merged?.visibleModules ?? []);
  const moduleActions = new Map<string, Set<string>>();
  const roleCodes = merged?.roleCodes ?? [];

  if (merged?.moduleActions) {
    for (const [moduleCode, actions] of Object.entries(merged.moduleActions)) {
      moduleActions.set(moduleCode, new Set(actions));
    }
  }

  return {
    hasModule: (code) => visibleModules.has(code),
    hasAction: (moduleCode, actionCode) =>
      moduleActions.get(moduleCode)?.has(actionCode) ?? false,
    getActions: (moduleCode) => moduleActions.get(moduleCode) ?? EMPTY_ACTIONS,
    getVisibleModules: () => visibleModules,
    getRoleCodes: () => roleCodes,
    isLoaded: () => merged !== null,
  };
}

/**
 * Decodes a JWT and returns the recsuit role codes from
 * `resource_access.recsuit.roles[]`. Returns empty array if the claim is
 * absent / malformed / the token isn't a JWT.
 *
 * Pure helper — used by AuthContext at login + on token refresh.
 */
export function extractRecsuitRolesFromJwt(token: string | null | undefined): string[] {
  if (!token) return [];
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return [];
    const payload = parts[1];
    // base64url → base64
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const decoded = atob(padded);
    const json = JSON.parse(decoded);
    const recsuitRoles = json?.resource_access?.recsuit?.roles;
    if (!Array.isArray(recsuitRoles)) return [];
    return recsuitRoles
      .filter((r: unknown): r is string => typeof r === 'string' && r.length > 0)
      .filter((r: string) => r.startsWith('recsuit_'));
  } catch {
    return [];
  }
}
